import { Box, Stack, SxProps } from '@mui/material';
import RichText from '../../../../RichText';
import Author from '../../../Author';
import { CardItemType, CardsType } from '../../type';
import Wrapper from './Wrapper';
import Tags from '../../../../tags';
import CardImage from './Image';
import HorizontalCard from './Horizontal';
import Optional from '../../../../Optional';
import getRichTextSummary from '../../../../RichText/getRichTextSummary';

type Props = CardItemType & {
  variant: string;
  role: CardsType['role'];
  sx?: SxProps;
  imageSx?: SxProps;
  orientation?: string;
  moveAuthorTop?: boolean;
  moveTagsTop?: boolean;
  isMobile: boolean;
};

const TextCard = (props: Props) => {
  const {
    title,
    image,
    link,
    tags,
    description,
    author,
    variant,
    sx,
    imageSx,
    flip,
    orientation,
    moveAuthorTop,
    moveTagsTop,
    role,
  } = props;
  const isStatic = variant === 'static';
  const shouldFlip = {
    mobile: ['all', 'mobile'].includes(flip!),
    desktop: ['all', 'desktop'].includes(flip!),
  };
  const direction = {
    md: shouldFlip.desktop ? 'column-reverse' : 'column',
    xs: shouldFlip.mobile ? 'column-reverse' : 'column',
  } as const;
  const imagePadding =
    role === 'team'
      ? {
          px: 2,
          pt: {
            md: shouldFlip.desktop ? 0 : 2,
            xs: shouldFlip.mobile ? 0 : 2,
          },
          pb: {
            md: shouldFlip.desktop ? 2 : 0,
            xs: shouldFlip.mobile ? 2 : 0,
          },
        }
      : undefined;
  if (orientation === 'horizontal') return <HorizontalCard {...props} />;

  const withDescription =
    !!getRichTextSummary(title) ||
    !!getRichTextSummary(description) ||
    !!tags?.length ||
    !!author;
  return (
    <Wrapper link={link}>
      <Stack
        className={'tb-card-text'}
        direction={direction}
        sx={{
          height: '100%',
          borderRadius: { xs: 0.5, md: 1 },
          pt: {
            md: image ? (shouldFlip.desktop ? 4 : 0) : 4,
            xs: image ? (shouldFlip.mobile ? 3 : 0) : 3,
          },
          pb: {
            md: image ? (shouldFlip.desktop ? 0 : 4) : 4,
            xs: image ? (shouldFlip.mobile ? 0 : 3) : 3,
          },
          bgcolor: 'background.paper',
          overflow: 'hidden',
          width: '100%',
          maxWidth: {
            xs: '100%',
          },
          justifyContent: 'space-between',
          color: 'text.primary',
          ...sx,
        }}
        spacing={1}
      >
        <CardImage img={image} sx={{ ...imagePadding, ...imageSx }} />
        <Optional condition={withDescription}>
          <Stack
            sx={{
              justifyContent: 'space-between',
              height: '100%',
            }}
            direction={moveTagsTop ? direction : 'column'}
            spacing={1}
          >
            <Stack
              spacing={2}
              direction={direction}
              sx={{
                px: { xs: 2, md: isStatic ? 4 : 3 },
              }}
            >
              <Optional condition={moveAuthorTop}>
                <Author author={author} hideAvatar variant={'horizontal'} />
              </Optional>
              <Box>
                <RichText
                  withTypography
                  variant="h4"
                  component="h2"
                  gutterBottom
                >
                  {title}
                </RichText>
                <RichText>{description}</RichText>
              </Box>
            </Stack>
            <Stack
              spacing={2}
              sx={{
                display: !moveAuthorTop || tags?.length ? 'flex' : 'none',
                px: { xs: 2, md: isStatic ? 4 : 3 },
              }}
            >
              <Tags tags={tags} />
              <Optional condition={!moveAuthorTop}>
                <Author author={author} hideAvatar={author?.hideAvatar} />
              </Optional>
            </Stack>
          </Stack>
        </Optional>
      </Stack>
    </Wrapper>
  );
};

export default TextCard;
